import type { IframeOptions } from '@blocksuite/affine-shared/services';
import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

import { renderExternalEmbedGate } from '../common/external-embed-gate.js';
import { DEFAULT_IFRAME_HEIGHT, DEFAULT_IFRAME_WIDTH } from './consts.js';

export interface IframeEmbedOptions {
  iframeUrl: string | undefined;
  iframeOptions: IframeOptions | undefined;
  inSurface: boolean;
  /** The reader released the website for this view. */
  loaded: boolean;
  onLoad: () => void;
}

/** Host of the embedded website, shown on the placeholder. */
export function iframeHost(iframeUrl: string | undefined): string | undefined {
  if (!iframeUrl) return undefined;
  try {
    return new URL(iframeUrl).host || undefined;
  } catch {
    return undefined;
  }
}

function iframeSize(
  { widthPercent, heightInNote }: Partial<IframeOptions>,
  inSurface: boolean
) {
  return {
    width:
      widthPercent === undefined ? DEFAULT_IFRAME_WIDTH : `${widthPercent}%`,
    // in the surface the block sets the size, in a note the provider height
    height: inSurface ? '100%' : (heightInNote ?? DEFAULT_IFRAME_HEIGHT),
  };
}

/**
 * Content of the iframe block once its data is known: the external-content
 * placeholder at the iframe's size until the reader loads the website, then
 * the iframe.
 */
export function renderIframeEmbed({
  iframeUrl,
  iframeOptions,
  inSurface,
  loaded,
  onLoad,
}: IframeEmbedOptions): TemplateResult {
  const options: Partial<IframeOptions> = iframeOptions ?? {};
  const { width, height } = iframeSize(options, inSurface);

  if (!loaded) {
    return html`<div
      class="affine-embed-iframe-gate"
      style=${styleMap(
        // in a note the placeholder may grow so the load button stays
        // visible on narrow pages; in the surface the block sets the size
        typeof height === 'number'
          ? { width, minHeight: `${height}px` }
          : { width, height }
      )}
    >
      ${renderExternalEmbedGate({
        provider: 'iframe',
        previewImageUrl: null,
        source: iframeHost(iframeUrl),
        onLoad,
      })}
    </div>`;
  }

  const { style, allow, referrerpolicy, scrolling, allowFullscreen } = options;
  return html`
    <iframe
      width=${width}
      height=${height}
      ?allowfullscreen=${allowFullscreen}
      loading="lazy"
      frameborder="0"
      credentialless
      src=${ifDefined(iframeUrl)}
      allow=${ifDefined(allow)}
      referrerpolicy=${ifDefined(referrerpolicy)}
      scrolling=${ifDefined(scrolling)}
      style=${ifDefined(style)}
    ></iframe>
  `;
}
